import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Routesmap from './routesfolder/Routesmap.jsx';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logOutStop } from './redux/reducers/logout';
import SnackbarComponent, { showSuccessSnackbar } from './components/snackBar/snakbar';
import { persistor } from './redux/store';
import { GlobalStyles } from '@mui/material';
import ReactGA from 'react-ga4';
import { registerLicense } from '@syncfusion/ej2-base';
import { useThemes } from './components/Theme/theme';
import { ThemeProviders } from './components/Theme/themeProviders';
import ConfirmExitNew from './components/modals/confirmExitNew';
import useEnterToNavigateInputs from './hooks/useEnterKeySpacial';
import { ENVIRONMENT } from './config.js';
import { setModalOpen } from './redux/reducers/controllNavigation.js';
import { useBroadcastChannel } from './hooks/useBroadcastChannel.js';
import { updateUserDetailsDevice } from './redux/reducers/userDetails.js';
import { fetchFeatureFlags } from './redux/reducers/featureFlagSlice';
import { borderRight } from '@mui/system';
import BroadcastReceiver from './components/BroadcastReceiver';
import {useRelativeLink} from './hooks/useRelativeLink';
import { WhatsAppSupportButton } from './components/_components';

// Registering Syncfusion license key
registerLicense('Ngo9BigBOggjHTQxAR8/V1NBaF5cXmZCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXlceHRXRGBcVERwW0s=');

export const globalColors = {
  primary: '#911B7C',
};

function App() {
  const { theme } = useThemes();
  useRelativeLink();

  // Memoize the global styles to prevent recreation on every render
  const globalScrollbarStyles = useCallback(
    (x, w) => (
      <GlobalStyles
        styles={{
          ':root': {
            '--global-main': globalColors.primary,
          },
          '.custom-popover-class': {
            backgroundColor: '#2d2d2d', // Custom background color
            color: '#fff', // Text color
            borderRadius: '8px', // Rounded corners
            padding: '16px', // Custom padding
          },
          '.actionBackGround': {
            background: theme?.palette?.TmId.id.toString().includes('9') ? '#7c7c7c !important' : 'white !important',
            right: '0',
            position: 'sticky',
            whiteSpace: 'nowrap',
            border: 'none !important',
          },
          '.MuiAutocomplete-popper .MuiAutocomplete-listbox': {
            // minHeight: '30px',
            maxHeight: '300px',
          },
          '.MuiAutocomplete-popper .MuiAutocomplete-listbox .MuiAutocomplete-option': {
            fontSize: '13px',
            minHeight: '25px',
            margin: '-7px 1px', // Adjust this value as needed to set the gap between items
          },
          '*::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            borderLeft: '3px solid',
            borderColor: theme.palette.bodyGray.main,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.bodyGray.main,
            boxShadow: 'none',
          },
          '.more-width::-webkit-scrollbar-thumb': {
            borderLeft: '1px solid ',
            borderColor: theme.palette.bodyGray.main,
          },

          '.custom-scrollbar::-webkit-scrollbar': {
            width: '11px',
            height: '11px',
            transition: 'width 0.3s ease',
          },
          '.a .MuiInputBase-root.Mui-focused': {
            backgroundColor: '#e5e5e5',
          },
          '.custom-scrollbar::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main,
            border: '3px solid',
            borderColor: theme.palette.bodyGray.main,
          },
          '.custom-scrollbar::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.bodyGray.main,
            boxShadow: 'none',
          },
          '.custom-scrollbar:hover::-webkit-scrollbar-thumb': {
            border: 'none',
            borderColor: theme.palette.background.paper,
          },
          '& .MuiAutocomplete-listbox': {
            // maxHeight: '300px', // adjust as needed
            overflowY: 'auto',
            '&:hover::-webkit-scrollbar': {
              cursor: 'pointer',
              border: 'none',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              border: 'none',
            },
            '&::-webkit-scrollbar': {
              width: '8px',
              transition: 'width 0.3s ease',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.bodyGray.main,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.main,
              border: '3px solid',
              borderColor: theme.palette.bodyGray.main,
            },
          },
          '.MuiInputBase-input::-webkit-scrollbar': {
            width: '8px', // Width of the scrollbar
            transition: 'width 0.3s ease', // Smooth transition
          },
          '.MuiInputBase-input::-webkit-scrollbar-track': {
            background: theme?.palette?.TmId.id.toString().includes('9') ? '#1E1E1E' : '#ffffff', // Track color
          },
          '.MuiInputBase-input::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.primary.main, // Thumb color
            border: `3px solid`, // Border color matching the track color
            borderColor: theme?.palette?.TmId.id.toString().includes('9') ? '#1E1E1E' : '#ffffff',
          },
          '.MuiInputBase-input::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: 'darken(scrollbarColor, 10%)', // Darker color on hover (adjust as needed)
          },
          kbd: {
            display: 'inline-block',
            padding: '2px 4px',
            margin: '0 1px',
            fontSize: '0.85em',
            fontFamily: 'monospace, sans-serif',
            color: 'black',
            border: '1px solid #ccc',
            borderRadius: '3px',
            backgroundColor: '#f8f8f8',
          },
          '.zoomLevel': {
            zoom: '1',

            '@media (max-width:600px)': {
              zoom: '.8',
            },
          },
          '.zoomLevelTable': {
            zoom: '1',

            '@media (max-width:600px)': {
              zoom: '.7',
            },
          },

        '.MuiFormControlLabel-labelPlacementEnd': {
          color: theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black',
        },
        '& input:-webkit-autofill': {
          '-webkit-box-shadow': '0 0 0 100px transparent inset !important',
          '-webkit-text-fill-color': `inherit ${theme?.palette?.TmId.id.toString().includes('9') ? 'white' : 'black'}!important`, // Make sure the text color matches
          // '-webkit-text-fill-color': 'inherit white !important',
          transition: 'background-color 5000s ease-in-out 0s',
        },
        "input[type='date']::-webkit-calendar-picker-indicator": {
          filter: theme?.palette?.TmId.id.toString().includes('9') ? 'invert(100%) brightness(100%)' : '',
          cursor: 'pointer',
        },
        "input[type='date']": {
          appearance: 'none', // Remove default styling
          color: theme.palette.TmId.id.toString().includes('9') ? '#fff' : '#000', // Text color inside the input
        },
       
        '.focusTableCellBorder .MuiTableCell-root:focus-within': {
          border: '3px solid #ffb74d !important',
        },
        '.MuiTableHead-root .MuiTableCell-root:focus-within': {
          background: 'transparent',
        },
        '.noTableCellBorder .MuiTableCell-root:focus-within': {
          border: 'none !important',
        },
        '.noTableCellBorder .MuiOutlinedInput-root.Mui-focused': {
          border: 'none !important',
        },
        '.noTableCellBorder .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
        },
        '.mergedEntryTableBody.noTableCellBorder .MuiTableCell-root.mergedCellActive': {
          border: '3px solid #ffb74d !important',
        },
        '.mergedEntryTableBody.noTableCellBorder .MuiTableCell-root:focus-within': {
          border: '3px solid #ffb74d !important',
        },
        '.mergedEntryTableBody.noTableCellBorder .MuiTableCell-root.mergedCellError': {
          border: '3px solid #d32f2f !important',
        },
        '.mergedEntryTableBody.noTableCellBorder .MuiTableCell-root.mergedCellError:focus-within': {
          border: '3px solid #d32f2f !important',
        },
        '.mergedEntryTableBody.noTableCellBorder .MuiTableCell-root.mergedCellActive.mergedCellError': {
          border: '3px solid #d32f2f !important',
        },
        '.TableCellBorder .MuiTableCell-root:focus-within': {
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          borderBottom: '1px solid #e0e0e0',
        },
        '.EnabeleTableCellBorder .MuiTableCell-root:focus-within': {
          border: '1px solid #e0e0e0',
        },
        '.EnabeleTableCellBorder .MuiOutlinedInput-root.Mui-focused': {
          backgroundColor: '#fff !important',
        },
        '.EnabeleTableCellBorder .MuiOutlinedInput-root.Mui-focused .MuiInputBase-input': {
          color: '#000 !important',
          cursor: 'text',
        },
        '.actionCellStyle': {
          whiteSpace: 'nowrap',
          // border:"solid 1.5px black",
          borderRadius: '10px',
          padding: '1px',
        },
        '.noFocusTableRow .MuiTableCell-root:focus-within': {
          outline: 'none !important',
        },
        '.noFocusTableRow .MuiTableCell-root': {
          borderTop: '1px solid #C0C0C0',
          borderBottom: '1px solid #C0C0C0',
          borderRight: '1px solid #C0C0C0',
          borderLeft: '1px solid #C0C0C0',
        },
        
  '.no-focus-outline:focus': {
    outline: 'none',
  },

          '.no-focus-outline:focus-visible': {
            outline: 'none',
          },
          '.saveButton':{
           backgroundColor: '#EE5B2B !important'
          }
        }}
      />
    ),
    [theme]
  );

  let navigate = useNavigate();
  let dispatch = useDispatch();
  const location = useLocation();
  const [nav, setNav] = useState(true);
  let Device = useSelector((state) => state?.userDetailsReducer?.userDetails?.Device);
  console.log('Device', Device);
  function HideScrollbar() {
    var style = document.createElement('style');
    style.innerHTML = `body::- webkit - scrollbar { display: none;}`;
    document.head.appendChild(style);
  }
  useEffect(() => {
    HideScrollbar();
    dispatch(logOutStop());

    if (ENVIRONMENT === 'production') {
      ReactGA.initialize('G-HGKNVJ8QDF');
      ReactGA.send({
        hitType: 'pageview',
        page: window.location.pathname + window.location.search,
      });
      ReactGA.event({
        category: 'User',
        action: 'Visited Site',
      });
      console.log('Production script');
    }
  }, []);

  const divRef = useRef(null);
  console.log('app render');

  useEffect(() => {
    const env = process.env.REACT_APP_ENV;
    const noIndexEnvs = ['QA', 'beta', 'development'];
    console.log(env);
    if (noIndexEnvs.includes(env || '')) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const ele = divRef.current;
      if (ele) {
        const distance = ele.offsetLeft + ele.offsetWidth - e.pageX;
        if (distance < 15 && distance > -15) {
          ele.classList.add('more-width');
        } else {
          ele.classList.remove('more-width');
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEnterToNavigateInputs();

  const allowNavigationRef = useRef(false);
  const { isBlocked } = useSelector((state) => state.controllNavigationSlice);

  useEffect(() => {
    if (isBlocked) {
      const st = window.history.state;
      window.history.pushState(st, document.title, window.location.href);
    }
  }, [location, isBlocked]);

  console.log(isBlocked, 'isBlocked');

  useEffect(() => {
    const onPopState = (event) => {
      if (!isBlocked) {
        return;
      }

      if (allowNavigationRef.current) {
        return;
      }

      dispatch(setModalOpen(true));
      const st = window.history.state;
      window.history.pushState(st, document.title, window.location.href);
    };

    if (isBlocked) {
      window.addEventListener('popstate', onPopState);
    }

    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [dispatch, isBlocked]);

  const handleSwitchCompany = (event) => {
    console.log(event, 'work');
    navigate('/');
    dispatch(updateUserDetailsDevice());
    showSuccessSnackbar(event);
  };
  useBroadcastChannel('app-events', {
    onMessage: (event) => {
      console.log(event);
    },
    onSwitchCompany: handleSwitchCompany,
  });

  return (
    <ThemeProviders>
      {globalScrollbarStyles(divRef?.current?.clientWidth - 10, window.innerWidth)}
      <BroadcastReceiver />

      <Box
        ref={divRef}
        id="element"
        sx={{
          background: theme.palette.bodyGray.main,
          // background: " rgb(255, 87, 131)",
          // background: "linear-gradient(161deg, rgba(255, 87, 131, 1) 0%, rgba(4, 174, 243, 1) 100%)",
          // background: "rgb(249,117,152)",
          // background: "linear-gradient(161deg, rgba(249,117,152,1) 0%, rgba(40,188,248,1) 100%)",
          minHeight: '100vh',
          overflow: 'auto',
          height: '100%',
        }}
      >
        <Box
          className="PageHeight"
          sx={{
            height: '100%',
            // width: window.innerWidth - 10,
            // background: theme.palette.bodyGray.main,
          }}
        >
          <Routesmap key={Device} nav={nav} />
        </Box>
      </Box>
      <SnackbarComponent />
      <ConfirmExitNew />
      <WhatsAppSupportButton />
    </ThemeProviders>
    // </ErrorBoundary>
  );
}

export default App;
